import {
  useCallback, useEffect, useState
} from "react";

function formatMmSs(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

/**
 * Countdown derived from an ISO ``expires_at`` timestamp, used by the
 * channel-pairing modals (SMS / iMessage / WhatsApp) to show how long a
 * verification code stays valid.
 *
 * Recomputes the remaining seconds whenever ``expiresAt`` changes, then ticks
 * down once per second. ``reset`` zeroes it on close/regenerate.
 */
export function useCountdown(expiresAt: string | null | undefined) {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (expiresAt && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [timeLeft, expiresAt]);

  useEffect(() => {
    if (expiresAt) {
      const expires = new Date(expiresAt);
      const now = new Date();
      const secondsLeft = Math.max(
        0,
        Math.floor((expires.getTime() - now.getTime()) / 1000),
      );
      setTimeLeft(secondsLeft);
    }
  }, [expiresAt]);

  const reset = useCallback(() => setTimeLeft(0), []);

  return { timeLeft, formatted: formatMmSs(timeLeft), reset };
}
